import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { MdClose, MdKeyboardArrowDown } from "react-icons/md";
import { 
  FiHome, FiInfo, FiCpu, FiBox, FiUsers, 
  FiBriefcase, FiMessageSquare, FiSend, FiArrowRight, FiImage, FiZap, FiBookOpen 
} from "react-icons/fi";
import werenium from "../assets/werenium.jpg";

export default function SideBar() {
  const appContext = useContext(AppContext);
  const { closeSideBar: closeSideBarFromContext, isSideBarOpen, navItem, navTo } = appContext;
  
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const closeSideBar = () => closeSideBarFromContext();
  const goTo = (key) => { navTo({ item: key }); closeSideBar(); };

  const navItems = [
    { key: "home", label: "Home", icon: <FiHome /> },
    { key: "services", label: "Services", icon: <FiCpu /> },
    { key: "products", label: "Products", icon: <FiBox /> },
    { key: "portfolio", label: "Portfolio", icon: <FiBriefcase /> },
    { key: "testimonials", label: "Testimonials", icon: <FiMessageSquare /> },
  ];

  useEffect(() => {
    if (!isSideBarOpen) return;
    const handleKeyDown = (e) => e.key === "Escape" && closeSideBarFromContext();
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeSideBarFromContext, isSideBarOpen]);

  const activeNavItem = navItem || "home";

  return (
    <aside className="SideBar" data-open={isSideBarOpen} aria-hidden={!isSideBarOpen}>
      <style>{`
        .side-modern-body {
          background: #000000 !important;
          border-left: 1px solid rgba(255,255,255,0.1);
          display: flex;
          flex-direction: column;
          padding: 0 !important;
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* Container for Logo + Absolute Close Button */
        .side-modern-head { 
          padding: 24px; 
          position: relative; /* Anchor for the absolute button */
        }

        .side-brand-wrapper {
          position: relative;
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
        }

        .side-close-btn {
          position: absolute;
          top: 12px;
          right: 12px;
          z-index: 10;
          background: rgba(0, 0, 0, 0.5); /* Semi-transparent black */
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          cursor: pointer;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .side-close-btn:hover {
          background: #fff;
          color: #000;
          transform: rotate(90deg);
        }

        .side-brand-img {
          width: 100%;
          height: auto;
          display: block;
          border-radius: 8px;
        }

        .side-nav-container { padding: 10px 16px 20px; flex-grow: 1; overflow-y: auto; }
        .nav-list { display: flex; flex-direction: column; gap: 6px; }

        .nav-link-btn {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 12px 18px;
          background: transparent;
          border: none;
          color: #999;
          font-size: 0.95rem;
          font-weight: 500;
          text-align: left;
          cursor: pointer;
          transition: all 0.25s ease;
          border-radius: 12px;
          width: 100%;
        }

        .nav-link-btn:hover { color: #fff; background: rgba(255,255,255,0.07); }

        .nav-link-btn[data-active="true"] {
          color: #000 !important;
          background: #fff !important;
          font-weight: 600;
          box-shadow: 0 4px 15px rgba(255,255,255,0.1);
        }
        .nav-link-btn[data-active="true"] .nav-icon { color: #000; }

        .sub-nav-wrapper {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding-left: 20px;
          margin: 4px 0 8px 24px;
          border-left: 1px solid rgba(255,255,255,0.1);
        }
        .sub-link-btn { padding: 10px 14px; font-size: 0.85rem; color: #777; }
        .sub-link-btn:hover { color: #fff; background: rgba(255,255,255,0.05); }

        .nav-icon { font-size: 18px; display: flex; align-items: center; }
        .chevron-icon { margin-left: auto; transition: 0.3s; }

        .side-footer { padding: 20px 16px 40px; }
        .footer-action {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          background: var(--secondaryColor, #fff);
          color: #000;
          border: none;
          padding: 16px 20px;
          font-weight: 700;
          border-radius: 12px;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          transition: 0.3s;
        }
        .footer-action:hover { filter: brightness(0.9); }
      `}</style>

      <button type="button" className="side-modern-overlay" onClick={closeSideBar} />

      <div className="body mNoScrollBar side-modern-body" role="dialog">
        <div className="side-modern-head">
          <div className="side-brand-wrapper">
            {/* CLOSE BUTTON ABSOLUTELY POSITIONED ON IMAGE */}
            <button className="side-close-btn" onClick={closeSideBar}>
              <MdClose size={18} />
            </button>
            <img src={werenium} alt="Logo" className="side-brand-img" />
          </div>
        </div>

        <div className="side-nav-container">
          <nav className="nav-list">
            <button 
              className="nav-link-btn" 
              onClick={() => goTo('home')}
              data-active={activeNavItem === 'home'}
            >
              <span className="nav-icon"><FiHome /></span>
              <span>Home</span>
            </button>

            {/* ABOUT GROUP */}
            <div className="nav-group">
              <button 
                className="nav-link-btn" 
                onClick={() => setIsAboutOpen(!isAboutOpen)}
                data-active={activeNavItem === 'about_us' || activeNavItem === 'team'}
              >
                <span className="nav-icon"><FiInfo /></span>
                <span>About Us</span>
                <MdKeyboardArrowDown 
                  size={20} 
                  className="chevron-icon"
                  style={{ transform: isAboutOpen ? 'rotate(180deg)' : 'rotate(0)' }} 
                />
              </button>
              
              {isAboutOpen && (
                <div className="sub-nav-wrapper">
                  <button className="nav-link-btn sub-link-btn" onClick={() => goTo('about_us')} data-active={activeNavItem === 'our_story'}>
                    <FiBookOpen size={14} /> <span>Our Story</span>
                  </button>
                  <button className="nav-link-btn sub-link-btn" onClick={() => goTo('team')} data-active={activeNavItem === 'team'}>
                    <FiUsers size={14} /> <span>Staff</span>
                  </button>
                </div>
              )}
            </div>

            {navItems.filter(i => i.key !== 'home').map((item) => (
              <button
                key={item.key}
                className="nav-link-btn"
                onClick={() => goTo(item.key)}
                data-active={activeNavItem === item.key}
              >
                <span className="nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}

            {/* MEDIA GROUP */}
            <div className="nav-group">
              <button 
                className="nav-link-btn" 
                onClick={() => setIsMediaOpen(!isMediaOpen)}
                data-active={activeNavItem === 'news' || activeNavItem === 'gallery'}
              >
                <span className="nav-icon"><FiZap /></span>
                <span>Media</span>
                <MdKeyboardArrowDown 
                  size={20} 
                  className="chevron-icon"
                  style={{ transform: isMediaOpen ? 'rotate(180deg)' : 'rotate(0)' }} 
                />
              </button>
              
              {isMediaOpen && (
                <div className="sub-nav-wrapper">
                  <button className="nav-link-btn sub-link-btn" onClick={() => goTo('news')} data-active={activeNavItem === 'news'}>
                    <FiMessageSquare size={14} /> <span>News</span>
                  </button>
                  <button className="nav-link-btn sub-link-btn" onClick={() => goTo('gallery')} data-active={activeNavItem === 'gallery'}>
                    <FiImage size={14} /> <span>Gallery</span>
                  </button>
                </div>
              )}
            </div>

            <button 
              className="nav-link-btn" 
              onClick={() => goTo('careers')}
              data-active={activeNavItem === 'careers'}
            >
              <span className="nav-icon"><FiBriefcase /></span>
              <span>Careers</span>
            </button>
          </nav>
        </div>

        <div className="side-footer">
          <button className="footer-action" onClick={() => goTo("contact_us")}>
            Start a Project <FiArrowRight size={18} />
          </button>
        </div>
      </div>
    </aside>
  );
}